export const setDecimalPlaces = (
  string: string,
  decimalPlaces: number
): string => {
  const formattedNumber = parseFloat(string);
  return formattedNumber.toFixed(decimalPlaces);
};

export const formatPercentageFromRatio = (
  number: string,
  decimalPlaces: number = 2
): string => {
  const mulipliedRatio = parseFloat(number) * 100;
  return mulipliedRatio.toFixed(decimalPlaces);
};

/**
 * Formats numbers with thousand separators and decimal handling
 * @param number - The number to format
 * @param isTwoDecimal - Force two decimal places with rounding
 * @param precision - Custom decimal precision
 * @param type - Formatting type: "SGD" for truncated decimals, "DEFAULT" for standard formatting
 */
export const formatNumbersWithThousandSeperators = (
  number: number,
  isTwoDecimal: boolean = false,
  precision?: number,
  type: "SGD" | "DEFAULT" = "DEFAULT"
): string => {
  // Handle SGD-specific formatting with truncation (no rounding)
  if (type === "SGD") {
    const truncated = Math.floor(number * 100) / 100;
    const parts = truncated.toString().split(".");
    const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const decPart = (parts[1] || "").padEnd(2, "0");
    return `${intPart}.${decPart}`;
  }

  // Handle precision-based formatting
  let formattedNumber;
  if (precision) {
    formattedNumber = number.toFixed(precision);
  } else {
    formattedNumber = number.toString();
  }

  const [integerPart, decimalPart] = formattedNumber.split(".");
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Handle two decimal formatting with rounding
  if (isTwoDecimal) {
    return Number(number)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  } else {
    return decimalPart !== undefined
      ? `${formattedInteger}.${decimalPart}`
      : formattedInteger;
  }
};

export const formatDate = (dateString: string) => {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = months[date.getMonth()];
  const day = date.getDate();

  return `${year} ${month} ${day}`;
};

// Helper: Format date to yyyy-mm-dd
export const formatDateDraft = (dateString: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  // Return empty string for Unix epoch year (1970)
  if (year === 1970) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Converts a date to UTC+8 (Singapore timezone) in 12-hour format
 * @param date - Date object, string, or any valid date input
 * @returns Formatted date string in "YYYY-MM-DD hh:mm AM/PM" format
 * @example
 * convertToUtcTwelveHours(new Date()) // "2024-03-15 02:30 PM"
 * convertToUtcTwelveHours("2024-03-15T14:30:00Z") // "2024-03-15 10:30 PM"
 */
export const convertToUtcTwelveHours = (date: any): string => {
  let resultedDate = "";
  if (date && date !== "-") {
    const formattedDate = (
      typeof date === "string" ? new Date(date) : date
    ).toLocaleString("en-SG", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    const [day, month, year] = formattedDate.split(",")[0].split("/");
    const time = formattedDate.split(",")[1].trim();
    resultedDate = `${year}-${month}-${day} ${time}`;
  }
  return resultedDate;
};
